import { HttpError, postJson } from '@/services/http';
import { mintRecaptchaToken, RecaptchaUnavailableError } from '@/services/recaptcha';

/**
 * Drives the contact modal: validation, submit, and the state transitions
 * between sending, thank-you and closed.
 *
 * Ported from the 2.2 controller. Behaviour changes worth knowing:
 *
 * - reCAPTCHA v3 runs here. v2 rendered a widget the visitor interacted with;
 *   v3 mints a token silently per submit, so the token is requested at submit
 *   time and posted with the form. The minting itself lives in
 *   services/recaptcha, shared with the contact-details reveal.
 * - Fetch replaces axios.
 * - The antiforgery token is sent as the RequestVerificationToken header, which
 *   is the name ASP.NET actually looks for. The 2.2 code sent
 *   "__RequestVerificationToken" — the form *field* name — so header validation
 *   never matched and the request relied on the cookie plus field alone.
 * - The success path no longer clears the form on a timer while the modal may
 *   already have been reopened.
 */

const EXIT_DELAY_MS = 4000;

export default class ContactFormController {
  private readonly modal: JQuery<HTMLElement>;
  private readonly form: JQuery<HTMLElement>;
  private exitTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(selector: string) {
    this.modal = window.$(selector);

    if (this.modal.length !== 1) {
      throw new Error(`ContactFormController expects exactly one target: ${selector}`);
    }

    this.form = this.modal.find('form');

    if (this.form.length !== 1) {
      throw new Error('ContactFormController expects exactly one form');
    }

    this.bindSubmit();
    this.form.find('#modal-form-cancel').first().on('click', () => this.close());
  }

  /**
   * Routes submission through jquery-validation rather than alongside it.
   *
   * A plain `.on('submit')` listener races validation's own handler, which is
   * bound first by unobtrusive validation on document ready. That race is what
   * let the browser perform a native POST: the page reloaded, the modal vanished
   * with it, and the submission never went through the AJAX path at all.
   *
   * submitHandler is validation's documented extension point. It fires only when
   * the form is valid, and validation suppresses the default itself, so there is
   * exactly one submit path rather than two competing ones.
   */
  private bindSubmit(): void {
    const validator = this.form.data('validator') as
      | { settings: { submitHandler?: (form: HTMLFormElement, event?: Event) => boolean } }
      | undefined;

    if (validator) {
      validator.settings.submitHandler = (_form, event) => {
        event?.preventDefault();
        void this.onSubmit();
        return false;
      };
      return;
    }

    // No unobtrusive validation on this form — submit directly, and guard the
    // native POST ourselves.
    this.form.on('submit', (event) => {
      event.preventDefault();
      void this.onSubmit();
    });
  }

  show(): void {
    this.exitThankYouMode();
    this.modal.modal('show');
  }

  private close(): void {
    this.clearExitTimer();
    this.modal.modal('hide');
  }

  private collectFormData(): Record<string, string> {
    const data: Record<string, string> = {};

    this.form.find('input:not([type=hidden]):not([type=submit]), textarea').each((_, element) => {
      const field = window.$(element);
      const name = field.attr('name');

      if (name) {
        data[name] = String(field.val() ?? '');
      }
    });

    return data;
  }

  private antiforgeryToken(): string | null {
    const field = this.form.find("input[name='__RequestVerificationToken']");
    const value = field.val();

    return typeof value === 'string' && value.length > 0 ? value : null;
  }

  private async recaptchaToken(): Promise<string | null> {
    return mintRecaptchaToken(
      this.form.data('recaptcha-site-key') as string | undefined,
      (this.form.data('recaptcha-action') as string | undefined) ?? 'contact_form',
    );
  }

  private pushAnalyticsEvent(): void {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({ event: 'SubmitContactUs' });
  }

  private enterSendingMode(): void {
    const submit = this.modal.find('input[type=submit]');
    submit.val('Sender..');
    submit.attr('disabled', 'disabled');
  }

  private exitSendingMode(): void {
    const submit = this.modal.find('input[type=submit]');
    submit.val('Send');
    submit.removeAttr('disabled');
  }

  private enterThankYouMode(): void {
    this.modal.find('#contact-modal-grid').hide();
    this.modal.find('#contact-modal-thankyou').transition('pulse');

    this.clearExitTimer();
    this.exitTimer = setTimeout(() => {
      this.resetForm();
      this.close();
    }, EXIT_DELAY_MS);
  }

  private exitThankYouMode(): void {
    this.clearExitTimer();
    this.modal.find('#contact-modal-thankyou').hide();
    this.modal.find('#contact-modal-grid').show();
  }

  private clearExitTimer(): void {
    if (this.exitTimer !== undefined) {
      clearTimeout(this.exitTimer);
      this.exitTimer = undefined;
    }
  }

  private resetForm(): void {
    this.form.find("input:not([type='submit']):not([type='hidden'])").val('');
    this.form.find('textarea').val('');
    this.setStatus('');
  }

  private setStatus(message: string): void {
    // textContent, so a server or network message can never inject markup.
    this.form.find('#contact-modal-action-status').text(message);
  }

  /** Called only once the form is known to be valid. */
  private async onSubmit(): Promise<void> {
    this.setStatus('');
    this.enterSendingMode();

    try {
      const payload: Record<string, string | null> = this.collectFormData();
      payload.recaptchaToken = await this.recaptchaToken();

      const token = this.antiforgeryToken();
      const headers: Record<string, string> = token ? { RequestVerificationToken: token } : {};

      await postJson('/kontakt-oss-sendinn', payload, headers);

      this.pushAnalyticsEvent();
      this.enterThankYouMode();
    } catch (error) {
      // The modal deliberately stays open on failure, showing why, so the
      // visitor can correct something or retry without retyping.
      this.setStatus(this.describeFailure(error));
      console.error('Kontaktskjema feilet', error);
    } finally {
      this.exitSendingMode();
    }
  }

  private describeFailure(error: unknown): string {
    if (error instanceof RecaptchaUnavailableError) {
      return 'Vi klarte ikke å laste robotsjekken. Last siden på nytt og prøv igjen, '
        + 'eller ta kontakt på telefon.';
    }

    if (error instanceof HttpError && error.status === 403) {
      return 'Vi klarte ikke å bekrefte at du er et menneske. Prøv gjerne igjen.';
    }

    return 'Beklager, det oppsto en feil. Prøv gjerne igjen, eller ta kontakt på telefon.';
  }
}
